import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Avatar,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Popconfirm,
  message,
  Modal,
  Form,
  Input,
} from 'antd';
import { UserOutlined, StopOutlined, CheckOutlined, EditOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';

interface User {
  id: number;
  fullName: string | null;
  phone: string;
  currentRole: 'CLIENT' | 'DRIVER' | 'ADMIN';
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  balance: number;
  bonusBalance: number;
  walletBalance: number;
  isDriverVerified: boolean;
  createdAt: string;
  blockedAt: string | null;
  blockedReason: string | null;
}

export const UserShow = () => {
  const { goBack, list, edit } = useNavigation();
  const { queryResult } = useShow<User>();
  const { data, isLoading, refetch } = queryResult;
  const user = data?.data;

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [editForm] = Form.useForm();

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      message.error('Sabab kiritilishi shart');
      return;
    }

    setBlocking(true);
    try {
      await apiClient.post(`/admin/users/${user?.id}/block`, {
        reason: blockReason,
      });
      message.success('Foydalanuvchi bloklandi');
      setBlockModalOpen(false);
      setBlockReason('');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    try {
      await apiClient.post(`/admin/users/${user?.id}/unblock`);
      message.success('Foydalanuvchi blokdan chiqarildi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleEdit = async (values: { fullName: string }) => {
    try {
      await apiClient.put(`/admin/users/${user?.id}`, values);
      message.success('Foydalanuvchi tahrirlandi');
      setEditModalOpen(false);
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  return (
    <>
      <Show
        isLoading={isLoading}
        title="Foydalanuvchi"
        headerButtons={
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                editForm.setFieldsValue({ fullName: user?.fullName || '' });
                setEditModalOpen(true);
              }}
            >
              Tahrirlash
            </Button>
            {user?.status === 'BLOCKED' ? (
              <Popconfirm
                title="Blokdan chiqarishni tasdiqlaysizmi?"
                onConfirm={handleUnblock}
              >
                <Button type="primary" icon={<CheckOutlined />}>
                  Blokdan chiqarish
                </Button>
              </Popconfirm>
            ) : (
              <Button danger icon={<StopOutlined />} onClick={() => setBlockModalOpen(true)}>
                Bloklash
              </Button>
            )}
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card>
              <div className="text-center">
                <Avatar
                  size={100}
                  src={user?.photoUrl}
                  icon={<UserOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <h3>{user?.fullName || 'Ismi yo\'q'}</h3>
                <p style={{ color: '#7F8C8D' }}>{user?.phone}</p>
                <Space>
                  <Tag
                    color={
                      user?.currentRole === 'DRIVER'
                        ? 'blue'
                        : user?.currentRole === 'ADMIN'
                          ? 'purple'
                          : 'default'
                    }
                  >
                    {user?.currentRole === 'DRIVER'
                      ? 'Haydovchi'
                      : user?.currentRole === 'ADMIN'
                        ? 'Admin'
                        : 'Mijoz'}
                  </Tag>
                  <Tag
                    color={
                      user?.status === 'ONLINE'
                        ? 'green'
                        : user?.status === 'BLOCKED'
                          ? 'red'
                          : 'default'
                    }
                  >
                    {user?.status}
                  </Tag>
                </Space>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card title="Statistika" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="Reyting" value={user?.rating != null ? Number(user.rating).toFixed(1) : '-'} />
                </Col>
                <Col span={6}>
                  <Statistic title="Sayohatlar" value={user?.totalTrips || 0} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Balans"
                    value={formatCurrency(user?.balance || 0)}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Bonus"
                    value={formatCurrency(user?.bonusBalance || 0)}
                  />
                </Col>
              </Row>
            </Card>

            <Card title="Ma'lumotlar">
              <Descriptions column={2}>
                <Descriptions.Item label="ID">{user?.id}</Descriptions.Item>
                <Descriptions.Item label="Telefon">{user?.phone}</Descriptions.Item>
                <Descriptions.Item label="To'liq ism">
                  {user?.fullName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Rol">
                  {user?.currentRole === 'DRIVER' ? 'Haydovchi' : 'Mijoz'}
                </Descriptions.Item>
                <Descriptions.Item label="Haydovchi tasdiqlangan">
                  {user?.isDriverVerified ? (
                    <Tag color="green">Ha</Tag>
                  ) : (
                    <Tag color="default">Yo'q</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Ro'yxatdan o'tgan">
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString('uz-UZ')
                    : '-'}
                </Descriptions.Item>
                {user?.blockedAt && (
                  <>
                    <Descriptions.Item label="Bloklangan sana">
                      {new Date(user.blockedAt).toLocaleDateString('uz-UZ')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bloklash sababi">
                      {user.blockedReason || '-'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </Show>

      {/* Block Modal */}
      <Modal
        title="Foydalanuvchini bloklash"
        open={blockModalOpen}
        onCancel={() => {
          setBlockModalOpen(false);
          setBlockReason('');
        }}
        onOk={handleBlock}
        okText="Bloklash"
        cancelText="Bekor qilish"
        okButtonProps={{ danger: true, loading: blocking }}
      >
        <p style={{ marginBottom: 16 }}>
          Foydalanuvchini bloklash sababini kiriting:
        </p>
        <Input.TextArea
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
          placeholder="Bloklash sababi..."
          rows={4}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Foydalanuvchini tahrirlash"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => editForm.submit()}
        okText="Saqlash"
        cancelText="Bekor qilish"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            label="To'liq ism"
            name="fullName"
            rules={[{ required: true, message: 'Ismni kiriting' }]}
          >
            <Input placeholder="Ismni kiriting" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
